/**
 * Cart Popup – confirmation popup after adding a product to the cart.
 * Shows product image, title, price (with compare-at), variant,
 * and a full-width "Ver cesta (X)" button.
 */

class CartPopup extends HTMLElement {
  constructor() {
    super();
    this.closeButton = this.querySelector('[data-popup-close]');
    this.cartButton = this.querySelector('[data-popup-cart]');
    this.autoCloseTimer = null;

    // product elements
    this.productImage = this.querySelector('[data-popup-image]');
    this.productImageWrapper = this.querySelector('[data-popup-image-wrapper]');
    this.productTitle = this.querySelector('[data-popup-title]');
    this.productVariant = this.querySelector('[data-popup-variant]');
    this.productPrice = this.querySelector('[data-popup-price]');
    this.productComparePrice = this.querySelector('[data-popup-compare-price]');
    this.cartCountEl = this.querySelector('[data-popup-cart-count]');
    this.imageRequestId = 0;
    this.oneSizeLabel = this.dataset.oneSizeLabel || '';
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    if (this.parentNode !== document.body) {
      document.body.appendChild(this);
    }

    this.closeButton?.addEventListener('click', () => this.hide());

    this.cartButton?.addEventListener('click', () => {
      this.hide();
      window.location.href = window.routes.cart_url;
    });

    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') {
        this.hide();
      }
    });

    this.addEventListener('click', (event) => {
      if (event.target === this) {
        this.hide();
      }
    });
  }

  /**
   * Show the popup with product data from the cart/add.js response
   * @param {Object} productData - Response from /cart/add.js
   */
  async show(productData) {
    if (!productData) return;

    // Cancel any pending auto-close timer
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }

    const imageRequestId = ++this.imageRequestId;
    const immediateImage = getImageSrc(productData.properties?._popup_image) || productData.image || '';
    this.renderProductImage(immediateImage, productData);

    // ── Título ──
    if (this.productTitle) {
      this.productTitle.textContent = productData.product_title || productData.title || '';
    }

    // ── Precio unitario del producto ──
    if (this.productPrice) {
      const unitPrice = productData.price || 0;
      this.productPrice.textContent = formatMoney(unitPrice);
    }

    if (this.productComparePrice) {
      this.productComparePrice.setAttribute('hidden', '');
    }

    // ── Variante ──
    if (this.productVariant) {
      const variantTitle = getPopupVariantTitle(productData, this.oneSizeLabel);
      this.productVariant.textContent = variantTitle;
      this.productVariant.style.display = variantTitle ? '' : 'none';
    }

    // ── Mostrar popup ──
    this.setAttribute('open', '');
    window.LolaCartPopupInteraction?.unlock();
    if (window.innerWidth <= 990) {
      bodyScroll?.lock?.(this.querySelector('.cart-popup__wrapper'));
    }

    // ── Botón "Ver cesta (X)" — con el total del carrito ──
    this.updateCartCount();

    this.getProductImage(productData).then(productImage => {
      if (imageRequestId !== this.imageRequestId || !this.hasAttribute('open')) return;
      this.renderProductImage(productImage, productData);
    });

    // ── Auto‑close tras 8 segundos ──
    this.autoCloseTimer = setTimeout(() => {
      this.hide();
    }, 8000);
  }

  async getProductImage(productData) {
    const metafieldImage = getImageSrc(productData.properties?._popup_image);
    if (metafieldImage) return metafieldImage;

    const fallbackImage = productData.image || '';
    const productUrl = productData.url?.split('?')[0];
    if (!productUrl) return fallbackImage;

    try {
      const response = await fetch(`${productUrl}.js`);
      const product = response.ok ? await response.json() : null;
      return product?.images?.find(image => image.toLowerCase().includes('0_silueteado')) || fallbackImage;
    } catch (error) {
      return fallbackImage;
    }
  }

  renderProductImage(imageSrc, productData) {
    if (!this.productImage) return;

    if (imageSrc) {
      this.productImageWrapper?.classList.add('is-loading');
      this.productImage.onload = () => {
        this.productImageWrapper?.classList.remove('is-loading');
      };
      this.productImage.onerror = () => {
        this.productImageWrapper?.classList.add('hidden');
        this.productImageWrapper?.classList.remove('is-loading');
      };
      this.productImage.src = imageSrc;
      this.productImage.alt = productData.product_title || productData.title || '';
      this.productImageWrapper?.classList.remove('hidden');
      if (this.productImage.complete) {
        this.productImageWrapper?.classList.remove('is-loading');
      }
    } else {
      this.productImageWrapper?.classList.add('hidden');
      this.productImageWrapper?.classList.remove('is-loading');
    }
  }

  /**
   * Fetch the total cart item count from /cart.js and update the counter display.
   */
  updateCartCount() {
    if (!this.cartCountEl) return;
    fetch(`${window.routes.cart_url}.js`)
      .then(response => response.json())
      .then(cart => {
        const totalQty = cart.item_count || 0;
        this.cartCountEl.textContent = `(${totalQty})`;
      })
      .catch(() => {
        // fallback silencioso
      });
  }

  hide() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    if (window.innerWidth <= 990) {
      bodyScroll?.unlock?.(this.querySelector('.cart-popup__wrapper'));
    }
    window.LolaCartPopupInteraction?.unlock();
    this.removeAttribute('open');
  }
}

customElements.define('cart-popup', CartPopup);

window.LolaCartPopupInteraction = window.LolaCartPopupInteraction || {
  lock() {
    document.body.classList.add('cart-popup-is-pending');
  },
  unlock() {
    document.body.classList.remove('cart-popup-is-pending');
  },
};

window.LolaCartError = window.LolaCartError || (() => {
  let hideTimer = null;
  return {
    show(response) {
      const toast = document.querySelector('[data-cart-error-toast]');
      if (!toast) return;
      const message = (response && (response.description || response.message)) || toast.dataset.defaultMessage;
      toast.querySelector('[data-cart-error-toast-text]').textContent = message;
      toast.removeAttribute('hidden');
      requestAnimationFrame(() => toast.classList.add('is-visible'));
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
        setTimeout(() => toast.setAttribute('hidden', ''), 300);
      }, 5000);
    },
  };
})();

function getImageSrc(image) {
  if (!image) return '';
  if (typeof image === 'string') return image;
  return image.src || image.url || image.preview_image?.src || '';
}

/**
 * Helper: format money value from cents to formatted price
 * Supports Shopify money format placeholders:
 *   {{amount}}                      → 19.99
 *   {{amount_with_comma_separator}}  → 19,99
 *   {{amount_no_decimals}}           → 20
 * @param {number} cents - Price in cents
 */
function formatMoney(cents) {
  if (typeof cents === 'string') {
    cents = cents.replace(/[^0-9.-]/g, '');
  }
  cents = parseFloat(cents) || 0;
  const value = (cents / 100).toFixed(2);
  const valueWithComma = value.replace('.', ',');

  const format = window.theme?.settings?.money_format || '€{{amount}}';

  return format
    .replace('{{amount_with_comma_separator}}', valueWithComma)
    .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)))
    .replace('{{amount}}', value);
}

function getPopupVariantTitle(productData, oneSizeLabel = '') {
  const rawVariantTitle = productData.variant_title || '';
  const normalizedOneSizeLabel = String(oneSizeLabel).trim().toLowerCase();
  const variantParts = rawVariantTitle
    .split('/')
    .map(part => part.trim())
    .filter(part => {
      const normalizedPart = part.toLowerCase();
      return Boolean(part)
        && normalizedPart !== 'u'
        && (!normalizedOneSizeLabel || normalizedPart !== normalizedOneSizeLabel);
    });
  const variantTitle = variantParts
    .join(' / ');
  const properties = productData.properties || {};
  const color = capitalizeText(properties._color || getOptionValue(productData, 'color') || getOptionValue(productData, 'colour'));

  if (!color) return variantTitle;
  if (!variantTitle) return color;
  if (variantTitle.toLowerCase().includes(String(color).toLowerCase())) return variantTitle;

  return `${color} ${variantTitle}`;
}

function getOptionValue(productData, optionName) {
  const options = productData.options_with_values || [];
  const option = options.find((item) => item.name?.toLowerCase() === optionName);
  return option?.value || '';
}

function capitalizeText(text) {
  return String(text || '')
    .trim()
    .toLowerCase()
    .replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());
}
